import { useState, useEffect } from "react";
import API from "@/lib/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BarChart3 } from "lucide-react";

export default function AnalyticsPage() {
  const [data, setData] = useState([]);

  useEffect(() => {
    API.get("/analytics/weekly?weeks=8")
      .then((res) => {
        setData(
          res.data.reverse().map((d) => ({
            ...d,
            week: new Date(d.week_start + "T00:00:00").toLocaleDateString(
              "en-US",
              { month: "short", day: "numeric" },
            ),
          })),
        );
      })
      .catch(() => {});
  }, []);

  const totalTasks = data.reduce((s, d) => s + d.total_tasks, 0);
  const totalCompleted = data.reduce((s, d) => s + d.completed_tasks, 0);
  const totalRollovers = data.reduce((s, d) => s + (d.rollovers || 0), 0);
  const avgRate =
    totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;

  return (
    <div data-testid="analytics-page">
      <h1 className="text-[28px] font-bold text-[#37352F] mb-1">Analytics</h1>
      <p className="text-[13px] text-[#6B6B6B] mb-6">How you're tracking against your weekly targets</p>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-8">
        <div className="p-4 border border-[#E0E0DE] rounded-[4px]" data-testid="total-targets-card">
          <p className="text-[12px] text-[#6B6B6B] mb-1">Total Tasks</p>
          <p className="text-2xl font-semibold text-[#37352F]">{totalTasks}</p>
        </div>
        <div className="p-4 border border-[#E0E0DE] rounded-[4px]" data-testid="completed-card">
          <p className="text-[12px] text-[#6B6B6B] mb-1">Completed</p>
          <p className="text-2xl font-semibold text-[#2383E2]">{totalCompleted}</p>
        </div>
        <div className="p-4 border border-[#E0E0DE] rounded-[4px]" data-testid="avg-rate-card">
          <p className="text-[12px] text-[#6B6B6B] mb-1">Completion Rate</p>
          <p className="text-2xl font-semibold text-[#37352F]">{avgRate}%</p>
        </div>
        <div className="p-4 border border-[#E0E0DE] rounded-[4px]" data-testid="rollovers-card">
          <p className="text-[12px] text-[#6B6B6B] mb-1">Rollovers</p>
          <p className="text-2xl font-semibold text-[#F2994A]">{totalRollovers}</p>
        </div>
      </div>

      {/* Chart */}
      {data.length > 0 && data.some((d) => d.total_tasks > 0) ? (
        <div
          className="border border-[#E0E0DE] rounded-sm p-6"
          data-testid="analytics-chart"
        >
          <h2 className="text-xl text-[#37352F] mb-4">
            Weekly Roster Completion
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0E0DE" />
              <XAxis
                dataKey="week"
                tick={{ fill: "#787774", fontSize: 12 }}
              />
              <YAxis tick={{ fill: "#787774", fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E0E0DE",
                  borderRadius: "2px",
                  fontFamily: "'Manrope', sans-serif",
                }}
                labelStyle={{
                  color: "#37352F",
                  fontFamily: "'Cormorant Garamond', serif",
                }}
              />
              <Bar
                dataKey="total_tasks"
                fill="#E0E0DE"
                name="Tasks"
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="completed_tasks"
                fill="#2383E2"
                name="Completed"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center py-16 border border-[#E0E0DE] rounded-sm">
          <BarChart3
            className="w-10 h-10 text-[#E0E0DE] mx-auto mb-3"
            strokeWidth={1}
          />
          <p className="text-[#787774] italic">
            No analytics data yet. Start adding tasks to your weekly roster and
            setting targets.
          </p>
        </div>
      )}
    </div>
  );
}
